import type { Language, NegativeReplyOption, NegativeReplyState } from "../types";
import { fmtDateTime } from "../format";

const LANGUAGE_LABELS: Record<Language, string> = {
  pt: "Português",
  en: "English",
  fr: "Français",
  es: "Español",
};

export interface NegativeReplyActionProps {
  status?: NegativeReplyState | null;
  options?: Record<Language, NegativeReplyOption>;
  language: Language;
  languageLocked: boolean;
  pending: boolean;
  error?: string | null;
  placement: "conversation" | "summary";
  onLanguageChange: (language: Language) => void;
  onSend: () => void;
}

export function NegativeReplyAction({
  status,
  options,
  language,
  languageLocked,
  pending,
  error,
  placement,
  onLanguageChange,
  onSend,
}: NegativeReplyActionProps) {
  const body = options?.[language]?.body || status?.body || "Texto indisponível.";
  const blocksSend = status?.status === "sent"
    || status?.status === "processing"
    || status?.status === "unknown";
  const buttonLabel = pending
    ? "ENVIANDO…"
    : status?.status === "sent"
      ? "NEGATIVA ENVIADA"
      : status?.status === "processing"
        ? "ENVIO EM PROCESSAMENTO"
        : status?.status === "unknown"
          ? "ENVIO A RECONCILIAR"
          : "RESPONDER NA NEGATIVA";

  function confirmSend() {
    if (blocksSend || pending) return;
    const confirmed = window.confirm(
      `Enviar esta resposta em ${LANGUAGE_LABELS[language]}?\n\n${body}`,
    );
    if (confirmed) onSend();
  }

  return (
    <section class={`negative-reply negative-reply--${placement}`} aria-label="Resposta negativa">
      <div class="negative-reply-heading">Resposta negativa</div>
      <p class="negative-reply-help">Mensagem fixa enviada pela equipe após a análise da solicitação.</p>
      {!status || status.status === "failed" ? (
        <>
          <label class="negative-reply-language">
            <span>Idioma</span>
            <select
              value={language}
              disabled={languageLocked || pending}
              onChange={(event) => onLanguageChange((event.currentTarget.value || "pt") as Language)}
            >
              {(Object.keys(LANGUAGE_LABELS) as Language[]).map((key) => (
                <option value={key} key={key}>{LANGUAGE_LABELS[key]}</option>
              ))}
            </select>
          </label>
          <div class="negative-reply-preview">{body}</div>
        </>
      ) : status.body ? (
        <div class="negative-reply-preview">{status.body}</div>
      ) : null}
      {status?.status === "sent" && (
        <p class="negative-reply-state">
          Enviada em {fmtDateTime(status.sent_at)} por {status.requested_by || "acesso autorizado"}
          {status.transport ? ` · ${status.transport === "text" ? "mensagem" : "template"}` : ""}
        </p>
      )}
      {status?.status === "unknown" && (
        <p class="negative-reply-state negative-reply-state--warning">
          O resultado do canal é incerto. O envio permanece bloqueado até reconciliação.
        </p>
      )}
      {status?.status === "failed" && (
        <p class="negative-reply-state negative-reply-state--warning">A tentativa anterior falhou e pode ser repetida.</p>
      )}
      {error && <p class="negative-reply-error">{error}</p>}
      <button class="btn negative-reply-button" disabled={blocksSend || pending} onClick={confirmSend}>
        {buttonLabel}
      </button>
    </section>
  );
}
